import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { DatePicker } from "./DatePicker";
import {
  formatDateInput,
  formatDateTimeInput,
  parseDateInput,
  getDateFormatPlaceholder,
  getDateTimeFormatPlaceholder,
  getTimeFormatPlaceholder,
  academyNowDate,
} from "../../utils/regionalFormat";

function pad(n) {
  return String(n).padStart(2, "0");
}

function toISODateTimeLocal(date) {
  if (!date) return "";
  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const hh = pad(date.getHours());
  const min = pad(date.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
}

function fromISODateTimeLocal(isoStr) {
  if (!isoStr) return null;
  const d = new Date(isoStr);
  return isNaN(d.getTime()) ? null : d;
}

export function DateTimePicker({ value, onChange, placeholder, className = "" }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [displayValue, setDisplayValue] = useState("");
  const wrapperRef = useRef(null);
  const defaultPlaceholder = placeholder || getDateTimeFormatPlaceholder();

  useEffect(() => {
    if (value) {
      const date = fromISODateTimeLocal(value);
      if (date) {
        setSelectedDate(date);
        setDisplayValue(formatDateTimeInput(date));
      }
    } else {
      setSelectedDate(null);
      setDisplayValue("");
    }
  }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleDateChange = (dateStr) => {
    const parsedDate = parseDateInput(dateStr);
    if (parsedDate) {
      const currentTime = selectedDate || academyNowDate();
      const newDate = new Date(
        parsedDate.getFullYear(),
        parsedDate.getMonth(),
        parsedDate.getDate(),
        currentTime.getHours(),
        currentTime.getMinutes()
      );
      setSelectedDate(newDate);
      setDisplayValue(formatDateTimeInput(newDate));
      onChange(toISODateTimeLocal(newDate));
    }
  };

  const handleTimeChange = (e) => {
    const timeValue = e.target.value;
    const currentDate = selectedDate || academyNowDate();

    if (timeValue) {
      const [hours, minutes] = timeValue.split(':').map(Number);
      const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), hours, minutes);
      setSelectedDate(newDate);
      setDisplayValue(formatDateTimeInput(newDate));
      onChange(toISODateTimeLocal(newDate));
    }
  };

  const handleClear = () => {
    setSelectedDate(null);
    setDisplayValue("");
    onChange("");
    setOpen(false);
  };

  const datePickerValue = selectedDate ? formatDateInput(selectedDate) : "";

  const timeInputValue = selectedDate
    ? `${pad(selectedDate.getHours())}:${pad(selectedDate.getMinutes())}`
    : "";

  const dateLabel = t('datePicker.dateWithFormat', { format: getDateFormatPlaceholder() });
  const timeLabel = t('datePicker.timeWithFormat', { format: getTimeFormatPlaceholder() });

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        value={displayValue}
        onClick={() => setOpen(!open)}
        readOnly
        placeholder={defaultPlaceholder}
        className={className || "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"}
      />

      {open && (
        <div className="absolute z-50 mt-1 p-4 bg-white border border-gray-300 rounded-lg shadow-lg" style={{ minWidth: '320px' }}>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{dateLabel}</label>
              <DatePicker
                value={datePickerValue}
                onChange={handleDateChange}
                className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{timeLabel}</label>
              <input
                type="time"
                value={timeInputValue}
                onChange={handleTimeChange}
                className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setOpen(false)}
                className="flex-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
              >
                {t('datePicker.done')}
              </button>
              <button
                onClick={handleClear}
                className="flex-1 px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
              >
                {t('datePicker.clear')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DateTimePicker;
